import React, { forwardRef, useCallback, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import Spacer from '@polestar/component-warehouse-react/components/spacer/Spacer';
import Orientations from '@polestar/component-warehouse-react/tokens/orientations/Orientations';
import Label from '@polestar/component-warehouse-react/components/label/Label';
import Icon from '@polestar/component-warehouse-react/components/icon/Icon';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import Text from '@polestar/component-warehouse-react/components/text/Text';
import { createCustomFontSize } from '@polestar/component-warehouse-react/tokens/font-sizes/FontSizes';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { getThemeId } from './Checkbox.helpers';
import Sizes from '@polestar/component-warehouse-react/tokens/sizes/Sizes';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import { minimalHitbox, minimalHitboxHeight, minimalHitboxWidth, } from '@polestar/component-warehouse-react/helpers/style';
import MinimalHitboxTypes from '@polestar/component-warehouse-react/tokens/minimal-hitbox-types/MinimalHitboxTypes';
import { focusProviderOptions } from '@polestar/component-warehouse-react/contexts/focus';
const Checkbox = forwardRef(({ onMouseEnter, onMouseLeave, name, error, errorLabel = false, disabled = false, indeterminate = false, checked, type = 'checkbox', label, minimalHitboxType = MinimalHitboxTypes.both, monochrome, ...props }, ref) => {
    const [hover, setHover] = useState(false);
    const callbackRef = useCallback((node) => {
        if (node) {
            node.indeterminate = indeterminate;
        }
    }, [indeterminate]);
    const combinedRef = useCombinedRef(ref, callbackRef);
    const handleLabelMouseEnter = useCallback((event) => {
        setHover(true);
        onMouseEnter === null || onMouseEnter === void 0 ? void 0 : onMouseEnter(event);
    }, [onMouseEnter]);
    const handleLabelMouseLeave = useCallback((event) => {
        setHover(false);
        onMouseLeave === null || onMouseLeave === void 0 ? void 0 : onMouseLeave(event);
    }, [onMouseLeave]);
    // choose the right theme
    const themeId = getThemeId(type);
    const mouseEvents = {
        onMouseEnter: handleLabelMouseEnter,
        onMouseLeave: handleLabelMouseLeave,
    };
    const checkboxContent = (React.createElement(StyledInputboxContainer, { type: type, disabled: disabled, checked: indeterminate || checked, error: error, hover: hover, minimalHitboxType: minimalHitboxType, monochrome: monochrome, ...(label ? mouseEvents : null) },
        React.createElement(StyledInput, { ...aria({
                disabled,
            }), ...props, ...focusProviderOptions({ round: type !== 'checkbox' }), ref: combinedRef, type: type, disabled: disabled, name: name, checked: !indeterminate && checked }),
        (checked || indeterminate) && (React.createElement(Mark, { themeId: themeId, disabled: disabled, error: error, indeterminate: indeterminate, monochrome: monochrome }))));
    return label !== undefined && label !== '' ? (React.createElement(StyledLabel, { ...mouseEvents, disabled: disabled, themeId: themeId, minimalHitboxType: minimalHitboxType },
        checkboxContent,
        label && React.createElement(Spacer, { orientation: Orientations.horizontal, spacing: Spacings.medium }),
        React.createElement(StyledText, { as: typeof label === 'string' ? 'span' : 'div', disabled: disabled, themeId: themeId, errorLabel: errorLabel }, label))) : (checkboxContent);
});
Checkbox.displayName = 'Checkbox';
export default Checkbox;
const StyledInput = styled.input `
  cursor: pointer;
  height: 100%;
  width: 100%;
  z-index: 2;
  opacity: 0;
  margin: 0;
`;
const StyledInputboxContainer = styled('div', shouldNotForwardProps(true, 'hover', 'type', 'disabled', 'error', 'checked', 'minimalHitboxType', 'monochrome'))(({ type, theme, disabled, error, checked, hover, minimalHitboxType, monochrome }) => {
    const themeId = getThemeId(type || 'checkbox');
    return css(css `
      display: inline-flex;
      flex-shrink: 0;
      justify-content: center;
      align-items: center;
      border-radius: ${type === 'radio' ? '50%' : 0};
      position: relative;
      width: ${Spacings.large};
      height: ${Spacings.large};
      border: 1px solid ${theme[themeId].states.enabled.borderColor};
      background-color: ${theme.checkbox.backgroundColor};
      -webkit-tap-highlight-color: transparent;
    `, minimalHitboxType === MinimalHitboxTypes.both && minimalHitbox, minimalHitboxType === MinimalHitboxTypes.width && minimalHitboxWidth, minimalHitboxType === MinimalHitboxTypes.height && minimalHitboxHeight, hover &&
        css `
        border-color: ${monochrome
            ? theme[themeId].states.hover.monochrome.borderColor
            : theme[themeId].states.hover.borderColor};
      `, checked &&
        css `
        border-color: ${monochrome
            ? theme[themeId].states.active.monochrome.borderColor
            : theme[themeId].states.active.borderColor};
      `, error &&
        css `
        border-color: ${theme[themeId].states.error.borderColor};
      `, disabled &&
        css `
        border-color: ${theme[themeId].states.disabled.borderColor};
        cursor: default;
        pointer-events: none;

        &:hover {
          cursor: default;
          border-color: ${theme[themeId].states.disabled.borderColor};
        }
      `, type === 'checkbox' &&
        checked &&
        css `
        border-color: transparent;
        background-color: ${monochrome
            ? theme[themeId].states.active.monochrome.backgroundColor
            : theme[themeId].states.active.backgroundColor};
      `, type === 'checkbox' &&
        checked &&
        hover &&
        css `
        border-color: transparent;
        background-color: ${monochrome
            ? theme[themeId].states.hover.monochrome.backgroundColor
            : theme[themeId].states.hover.backgroundColor};
      `, type === 'checkbox' &&
        checked &&
        disabled &&
        css `
        border-color: transparent;
        background-color: ${theme[themeId].states.disabled.backgroundColor};
      `, type === 'checkbox' &&
        checked &&
        error &&
        css `
        border-color: transparent;
        background-color: ${theme[themeId].states.error.backgroundColor};
      `);
});
const StyledLabel = styled(Label)(({ theme, disabled, themeId, minimalHitboxType }) => css(css `
        cursor: pointer;
        max-width: 100%;
        width: auto;
        display: flex;
        user-select: none;
        padding: ${Spacings.xSmall} 0;
        color: ${disabled
    ? theme[themeId].states.disabled.labelColor
    : theme[themeId].states.enabled.labelColor};
        position: relative;
      `, minimalHitboxType === MinimalHitboxTypes.both && minimalHitbox, minimalHitboxType === MinimalHitboxTypes.width && minimalHitboxWidth, minimalHitboxType === MinimalHitboxTypes.height && minimalHitboxHeight));
const Mark = ({ disabled, error, themeId, indeterminate = false, monochrome }) => themeId === 'checkbox' ? (React.createElement(StyledCheckboxMark, { disabled: disabled, error: error, themeId: themeId, monochrome: monochrome },
    React.createElement(Icon, { icon: indeterminate ? Icons.minus : Icons.checkmark, size: Sizes.medium }))) : (React.createElement(StyledRadioMark, { disabled: disabled, error: error, themeId: themeId, monochrome: monochrome }));
const StyledCheckboxMark = styled.div(({ theme, themeId, error, disabled, monochrome }) => css(css `
        position: absolute;
        inset-block-start: 0;
        inset-inline-start: 0;
        inset-inline-end: 0;
        inset-block-end: 0;
        color: ${monochrome
    ? theme[themeId].states.active.monochrome.markColor
    : theme[themeId].states.active.markColor};
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${createCustomFontSize(0.875)};
      `, disabled &&
    css `
          color: ${theme[themeId].states.disabled.markColor};
        `, !disabled &&
    error &&
    css `
          color: ${theme[themeId].states.error.markColor};
        `));
// a createCustomSpacing is needed due to the border (which subtracts 2px) therefore 14px / available 22px
const StyledRadioMark = styled.div(({ theme, themeId, disabled, error, monochrome }) => css(css `
      position: absolute;
      width: ${(14 / 22) * 100}%;
      height: ${(14 / 22) * 100}%;
      padding: ${createCustomSpacing(6 / 16)};
      border-radius: 50%;
      background-color: ${monochrome
    ? theme[themeId].states.active.monochrome.markColor
    : theme[themeId].states.active.markColor}; /* TODO: align with new theme setup*/
    `, disabled &&
    css `
        background-color: ${theme[themeId].states.disabled.markColor};
      `, error &&
    css `
        background-color: ${theme[themeId].states.error.markColor};
      `));
const StyledText = styled(Text, shouldNotForwardProps(false, 'disabled'))(({ theme, disabled, themeId, errorLabel }) => css `
    margin-block-start: ${Spacings.xxSmall};
    color: ${errorLabel
    ? theme[themeId].states.error.labelColor
    : disabled
        ? theme[themeId].states.disabled.labelColor
        : theme[themeId].states.enabled.labelColor};
    position: relative;
    z-index: 1;
  `);
